import { apis } from '@doltech/core/lib/api/api.config';
import { apiUrls } from '@doltech/core/lib/api/api.urls';
import { useUserTracker } from '@doltech/core/lib/hooks/withCustomerIO';
import { queryClient } from '@doltech/core/lib/hooks/withReactQueryConfig';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { buildInviteReferralLink } from '@doltech/utils/lib/url';
import { Image, Modal, Space } from 'antd';
import moment from 'moment';

import { get, noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import { flow, types as t } from 'mobx-state-tree';
import { useCreateStore, useProvider, useStore } from 'mobx-store-provider';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';

import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';
import { tuhocPrice } from '@doltech/core/lib/entity/tuhoc-price';
import { colorsV2 } from '../../figma/colors-v2';
import { Link } from '../../figma/Link';
import { ResponsiveTypography } from '../../figma/Typography/v3/ResponsiveTypography';
import { useDeviceDetect } from '../useDeviceQuery.hook';
import { useHistoryRouter } from '../useHistoryRouter';
import dolProImage from './svg/DolProStart.png';
import { EnergyElectricIcon } from './svg/EnegyElectricIcon';

const Left = styled.div`
  width: 100%;
  border: 1px solid ${colorsV2.blue40};
  background: ${colorsV2.blue10};
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);

  img {
    height: 85px;
    width: 85px;
  }

  @media (max-width: 540px) {
    img {
      display: none;
    }

    padding: 12px;
    gap: 0;
  }

  display: grid;
  grid-template-columns: max-content auto max-content;
  gap: 12px;
  align-items: center;
`;
const CloseIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${colorsV2.gray20};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const InboxDolBtn = styled.div`
  background: ${colorsV2.blue100};
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  height: 40px;
  color: ${colorsV2.white100};
  width: fit-content;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
`;

interface DolPremiumProps {
  onDismissExpiredModal?: ({ historyRouter, props, router }) => void;
}

export const DolPremiumStore = t
  .model('DolPremiumStore', {
    isExpired: false,
    isTrial: false,
    isChecking: true,
    expireAt: t.maybeNull<any>(t.string),
    inviteUrl: '',
    modalVisible: false,
    validDaysLeft: t.maybeNull<any>(t.number),
  })
  .volatile(() => ({
    lastFetchedSuccess: 0,
  }))
  .actions((self) => ({
    setModalVisible(newValue) {
      self.modalVisible = newValue;
    },
    setInviteUrl(newValue) {
      self.inviteUrl = newValue;
    },
    setChecking(newValue) {
      self.isChecking = newValue;
    },
    setExpired(newValue) {
      self.isExpired = newValue;
    },
    setIsTrial(newValue) {
      self.isTrial = newValue;
    },
    setExpiredAt(newValue) {
      self.expireAt = newValue;
    },
    setValidDaysLeft(newValue) {
      self.validDaysLeft = newValue;
    },
  }))
  .actions((self) => ({
    check: flow(function* run() {
      const staleData = queryClient.getQueryState('GET_MY_PREMIUM_USER_INFO');

      if (new Date().getTime() - self.lastFetchedSuccess < 60 * 1000) {
        return;
      }
      self.setChecking(true);
      try {
        let res = staleData?.data;
        if (!res) {
          res = yield apis.membership.get(apiUrls.membership.GET_MY_PREMIUM_USER_INFO);
          queryClient.setQueryData('GET_MY_PREMIUM_USER_INFO', res);
        }
        const expired = get(res, 'data.dpStatus') === 'EXPIRED';
        const expiredAt = get(res, 'data.dpExpireAt');
        self.setExpired(expired);
        self.setIsTrial(!get(res, 'data.everPurchased'));
        self.setExpiredAt(expiredAt);
        self.setValidDaysLeft(moment(expiredAt).diff(moment(), 'days'));
        self.setChecking(false);
        self.setInviteUrl(buildInviteReferralLink(get(res, 'data.referralCode', '')));
        self.lastFetchedSuccess = new Date().getTime();
        return {
          ok: !expired,
          isExpired: expired,
        };
        // eslint-disable-next-line no-empty
      } catch (ex) {}
      self.setChecking(false);
    }),
  }));

export const useDolPremiumStore = () => {
  return useStore(DolPremiumStore);
};

interface DolPremiumWrapperProps {
  children: any;
  onDismissExpiredModal?: (input: any) => void;
  externalProps: any;
}

const DolPremiumWrapper = observer(
  ({ children, onDismissExpiredModal, externalProps }: DolPremiumWrapperProps) => {
    const store = useDolPremiumStore();
    const { updateUserAttribute } = useUserTracker();
    const { isLoggedIn } = useGetAuthInfo();
    const { isMobile } = useDeviceDetect();
    const historyRouter = useHistoryRouter();
    const router = useRouter();

    React.useEffect(() => {
      if (isLoggedIn) {
        store.check();
      } else {
        store.setChecking(false);
      }
    }, [isLoggedIn, store]);

    const onDismiss = React.useCallback(
      (e) => {
        e.stopPropagation();
        store.setModalVisible(false);
        onDismissExpiredModal({
          props: externalProps,
          router,
          historyRouter,
        });
      },
      [store, onDismissExpiredModal, externalProps, router, historyRouter]
    );

    const renderTrialUI = React.useCallback(() => {
      return (
        <Grid columns={1} gap="24px">
          <Grid columns="auto max-content" gap="4px">
            <Space direction="vertical" size={4}>
              <ResponsiveTypography.Text variant="semi-bold/20">
                Tài khoản của bạn đã hết hạn xài thử
              </ResponsiveTypography.Text>
              <ResponsiveTypography.Text variant="regular/14">
                Gia hạn hoặc đổi DOL Points để tiếp tục
              </ResponsiveTypography.Text>
            </Space>
            <CloseIcon onClick={onDismiss}>
              <IconOtherClose font-size={isMobile ? 16 : 24} />
            </CloseIcon>
          </Grid>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space direction="horizontal" size={8}>
              <EnergyElectricIcon style={{ marginTop: '4px' }} />
              <ResponsiveTypography.Text variant="semi-bold/16">
                Gia hạn DOL Pro
              </ResponsiveTypography.Text>
            </Space>
            <Left>
              <Image src={dolProImage} preview={false} />
              <Space direction="vertical" size={0}>
                <ResponsiveTypography.Text variant="semi-bold/14">
                  DOL Pro
                </ResponsiveTypography.Text>
                <ResponsiveTypography.Text variant="semi-bold/16">
                  {tuhocPrice.DOLPRO_1M}{' '}
                  <ResponsiveTypography.Text color="black80" variant="semi-bold/14">
                    / 1 tháng
                  </ResponsiveTypography.Text>
                </ResponsiveTypography.Text>
              </Space>
              <Link href="https://m.me/dolvn" target="_blank" rel="nofollow">
                <InboxDolBtn>Inbox DOL</InboxDolBtn>
              </Link>
            </Left>
          </Space>
        </Grid>
      );
    }, [isMobile, onDismiss]);

    const renderExpiredUI = React.useCallback(() => {
      return (
        <Grid columns={1} gap="16px">
          <Grid columns="auto max-content" gap="4px">
            <Space direction="vertical" size={4}>
              <ResponsiveTypography.Text variant="semi-bold/20" color="red100">
                Tài khoản DOL Pro của bạn đã hết hạn !!!
              </ResponsiveTypography.Text>
            </Space>
            <CloseIcon onClick={onDismiss}>
              <IconOtherClose font-size={isMobile ? 16 : 24} />
            </CloseIcon>
          </Grid>

          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space direction="horizontal" size={8}>
              <EnergyElectricIcon style={{ marginTop: '4px' }} />
              <ResponsiveTypography.Text variant="semi-bold/16">
                Gia hạn DOL Pro
              </ResponsiveTypography.Text>
            </Space>
            <Left>
              <Image src={dolProImage} preview={false} />
              <Space direction="vertical" size={0}>
                <ResponsiveTypography.Text variant="semi-bold/14">
                  DOL Pro
                </ResponsiveTypography.Text>
                <ResponsiveTypography.Text
                  breakpoints={[0, 768, 1440]}
                  variant={['semi-bold/24', 'semi-bold/24', 'semi-bold/16']}
                >
                  {tuhocPrice.DOLPRO_1M}{' '}
                  <ResponsiveTypography.Text color="black80" variant="semi-bold/14">
                    / 1 tháng
                  </ResponsiveTypography.Text>
                </ResponsiveTypography.Text>
              </Space>
              <Link href="https://m.me/dolvn" target="_blank" rel="nofollow">
                <InboxDolBtn>Inbox page</InboxDolBtn>
              </Link>
            </Left>
          </Space>
        </Grid>
      );
    }, [isMobile, onDismiss]);

    const renderContent = React.useCallback(() => {
      if (store.isChecking) return null;
      if (store.isExpired) {
        return renderExpiredUI();
      } else if (store.isTrial) {
        return renderTrialUI();
      }
      return null;
    }, [renderExpiredUI, renderTrialUI, store.isChecking, store.isExpired, store.isTrial]);

    React.useEffect(() => {
      if (store.expireAt) {
        updateUserAttribute({
          is_used_premium_feature: true,
          last_used_premium_feature_date: Math.floor(Date.now() / 1000),
          premium_feature_end_date: Math.floor(new Date(store.expireAt).getTime() / 1000),
          referral_invite_url: store.inviteUrl,
        });
      }
    }, [store.expireAt, store.inviteUrl, updateUserAttribute]);

    return (
      <>
        {children}
        {store.modalVisible && (
          <Modal
            maskClosable={false}
            className="dol-modal"
            closable={false}
            width={isMobile ? 520 : 620}
            open={store.modalVisible}
            bodyStyle={isMobile ? { padding: '16px' } : { padding: '24px' }}
            footer={null}
            onCancel={onDismiss}
            closeIcon={undefined}
          >
            {renderContent()}
          </Modal>
        )}
      </>
    );
  }
);

export const withDolPremiumStore =
  (dolPremiumProps: DolPremiumProps) => (Component: any) => (props: any) => {
    const dolPremiumCreateStore = useCreateStore(DolPremiumStore, {});
    const DolPremiumStoreProvider = useProvider(DolPremiumStore);
    const { onDismissExpiredModal = noop } = dolPremiumProps;

    return (
      <DolPremiumStoreProvider value={dolPremiumCreateStore}>
        <DolPremiumWrapper externalProps={props} onDismissExpiredModal={onDismissExpiredModal}>
          <Component {...props} />
        </DolPremiumWrapper>
      </DolPremiumStoreProvider>
    );
  };
